import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/base/buttons/button';
import { FormField, HookForm } from '@/components/base/form/hook-form';

import { AUTH_BUTTON_CLASS, AUTH_INPUT_CLASS, AUTH_LABEL_CLASS } from './auth-styles';
import { AuthTextField } from './auth-text-field';

import type { LoginFormData } from './login-form-schema';
import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';

interface LoginEntryLayoutProps {
  readonly desktop: boolean;
  readonly isDisabled: boolean;
  readonly form: UseFormReturn<LoginFormData>;
  readonly methods: ReactNode;
  readonly children: ReactNode;
}

export function LoginEntryLayout({
  desktop,
  isDisabled,
  form,
  methods,
  children,
}: LoginEntryLayoutProps) {
  const { t } = useTranslation('auth');
  const [hasContinued, setHasContinued] = useState(false);

  useEffect(() => {
    if (hasContinued) {
      form.setFocus('password');
    }
  }, [hasContinued, form]);

  if (desktop) {
    return (
      <>
        <h1 className="sr-only">{t('login.heading')}</h1>
        {methods}
        <div className="my-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-entry-ink/12" />
          <span className="text-xs font-medium text-entry-ink/45">
            {t('desktopLogin.emailDivider')}
          </span>
          <div className="h-px flex-1 bg-entry-ink/12" />
        </div>
        {children}
      </>
    );
  }

  return (
    <>
      <div className={`${hasContinued ? 'mb-3' : 'mb-6'} text-center`}>
        <h1>
          <span className="sr-only">{t('webEntry.welcome')}</span>
          <span aria-hidden="true" className="flex flex-col items-center gap-1">
            <span className="text-xs font-medium text-tertiary">{t('webEntry.welcomePrefix')}</span>
            <span
              className="block h-9 w-40 bg-[var(--color-brand-logo-fg)]"
              style={{
                mask: 'url(/images/auth/wordmark.svg) center / contain no-repeat',
                WebkitMask: 'url(/images/auth/wordmark.svg) center / contain no-repeat',
              }}
            />
          </span>
        </h1>
        {hasContinued ? (
          <p className="mt-2 text-sm text-tertiary">{t('webEntry.passwordPrompt')}</p>
        ) : null}
      </div>
      {hasContinued ? (
        children
      ) : (
        <HookForm
          className="flex flex-col gap-4"
          form={form}
          onSubmit={(event) => {
            event.preventDefault();
            void (async () => {
              if (!isDisabled && (await form.trigger('email', { shouldFocus: true }))) {
                setHasContinued(true);
              }
            })();
          }}
        >
          <FormField control={form.control} name="email">
            {({ field, fieldState }) => (
              <AuthTextField
                {...field}
                autoComplete="username"
                hint={fieldState.error?.message}
                inputClassName={`${AUTH_INPUT_CLASS} h-12`}
                isDisabled={isDisabled}
                isInvalid={fieldState.invalid}
                label={t('webEntry.workEmail')}
                labelClassName={AUTH_LABEL_CLASS}
                placeholder={t('webEntry.emailPlaceholder')}
                type="email"
              />
            )}
          </FormField>
          <Button
            className={`${AUTH_BUTTON_CLASS} h-12 rounded-lg`}
            isDisabled={isDisabled}
            size="lg"
            type="submit"
          >
            {t('webEntry.continue')}
          </Button>
        </HookForm>
      )}
      <div className={`${hasContinued ? 'my-4' : 'my-5'} flex items-center gap-3`}>
        <div className="h-px flex-1 bg-border-secondary" />
        <span className="text-xs text-tertiary">{t('webEntry.otherMethods')}</span>
        <div className="h-px flex-1 bg-border-secondary" />
      </div>
      <div className={`grid grid-cols-2 gap-3 ${hasContinued ? 'sm:grid-cols-4' : ''}`}>
        {methods}
      </div>
    </>
  );
}
